import { useRef, useState } from "react";
import {
  MdAdd,
  MdAssignment,
  MdClose,
  MdCloudUpload,
  MdDeleteOutline,
  MdDescription,
  MdLocalShipping,
  MdRefresh,
  MdSearch,
  MdSettings,
} from "react-icons/md";
import NewArticle from "../ArticleManagement/NewArticle";
import "./GoodsReceiptBooking.scss";

function getToday() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

export default function GoodsReceiptBooking() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deliveryDate] = useState(getToday);
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [documentError, setDocumentError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);

  function selectDocument(file: File | null) {
    setIsDragging(false);
    if (!file) return;

    if (fileInputRef.current) fileInputRef.current.value = "";

    if (file.type !== "application/pdf" && !file.type.startsWith("image/")) {
      setDocumentError("Bitte wählen Sie eine PDF-Datei oder ein Bild aus.");
      return;
    }

    setDocumentError("");
    setSelectedDocument(file);
  }

  return (
    <section className="sales-layout__page goods-receipt-booking" aria-labelledby="goods-receipt-booking-title">
      <header className="goods-receipt-booking__header">
        <h1 id="goods-receipt-booking-title">Wareneingang buchen</h1>
        <p>Erfassen Sie den Lieferschein und die eingegangenen Artikel. Pflichtfelder sind mit * markiert.</p>
      </header>

      <form className="goods-receipt-booking__form" onSubmit={(event) => event.preventDefault()}>
        <div className="goods-receipt-booking__top-grid">
          <section className="goods-receipt-booking__section" aria-labelledby="receipt-delivery-title">
            <h2 id="receipt-delivery-title" className="goods-receipt-booking__section-title">
              <MdLocalShipping aria-hidden="true" /> Lieferschein Informationen
            </h2>
            <div className="goods-receipt-booking__section-content">
              <div className="goods-receipt-booking__field-row">
                <div className="goods-receipt-booking__field">
                  <label htmlFor="receipt-delivery-number">Lieferschein Nr. *</label>
                  <input id="receipt-delivery-number" name="deliveryNumber" type="text" placeholder="Lieferscheinnummer" required />
                </div>
                <div className="goods-receipt-booking__field">
                  <label htmlFor="receipt-delivery-date">Datum *</label>
                  <input id="receipt-delivery-date" name="deliveryDate" type="date" defaultValue={deliveryDate} required />
                </div>
              </div>

              <div className="goods-receipt-booking__distributor-row">
                <div className="goods-receipt-booking__field">
                  <label htmlFor="receipt-distributor">Distributor *</label>
                  <select id="receipt-distributor" name="distributor" defaultValue="" aria-describedby="receipt-distributor-hint" required disabled>
                    <option value="">Noch keine Distributoren verfügbar</option>
                  </select>
                </div>
                <button
                  type="button"
                  className="goods-receipt-booking__icon-btn"
                  aria-label="Distributoren aktualisieren"
                  title="Distributoren sind noch nicht angebunden"
                  disabled
                >
                  <MdRefresh aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="goods-receipt-booking__btn goods-receipt-booking__btn--secondary"
                  title="Die Distributorverwaltung ist noch nicht angebunden"
                  disabled
                >
                  Verwalten
                </button>
              </div>
              <p id="receipt-distributor-hint" className="goods-receipt-booking__hint">
                Die Distributorverwaltung wird später angebunden.
              </p>

              <div className="goods-receipt-booking__field">
                <label htmlFor="receipt-note">Notiz</label>
                <textarea id="receipt-note" name="note" rows={3} placeholder="Notiz zum Lieferschein" />
              </div>
            </div>
          </section>

          <section className="goods-receipt-booking__section" aria-labelledby="receipt-document-title">
            <h2 id="receipt-document-title" className="goods-receipt-booking__section-title">
              <MdDescription aria-hidden="true" /> Dokument hochladen
            </h2>
            <div className="goods-receipt-booking__section-content">
              <label
                className={`goods-receipt-booking__dropzone${isDragging ? " goods-receipt-booking__dropzone--dragging" : ""}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "copy";
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  selectDocument(event.dataTransfer.files[0] ?? null);
                }}
              >
                <input
                  ref={fileInputRef}
                  name="deliveryDocument"
                  type="file"
                  accept="application/pdf,image/*"
                  aria-label="Lieferscheindokument auswählen"
                  aria-describedby={`receipt-document-hint${documentError ? " receipt-document-error" : ""}`}
                  aria-invalid={Boolean(documentError)}
                  onChange={(event) => selectDocument(event.target.files?.[0] ?? null)}
                />
                <MdCloudUpload aria-hidden="true" />
                <span>{isDragging ? "Datei hier ablegen" : "Datei auswählen"}</span>
                <small>PDF oder Bild hierher ziehen oder auswählen</small>
              </label>

              <div aria-live="polite">
                {selectedDocument && (
                  <div className="goods-receipt-booking__document">
                    <MdDescription aria-hidden="true" />
                    <span>{selectedDocument.name}</span>
                    <button
                      type="button"
                      className="goods-receipt-booking__icon-btn"
                      aria-label="Ausgewähltes Dokument entfernen"
                      onClick={() => {
                        setSelectedDocument(null);
                        setDocumentError("");
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >
                      <MdClose aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
              {documentError && (
                <p id="receipt-document-error" className="goods-receipt-booking__error" role="alert">
                  {documentError}
                </p>
              )}
              <p id="receipt-document-hint" className="goods-receipt-booking__hint">
                Das Dokument wird nur lokal ausgewählt und noch nicht hochgeladen.
              </p>
            </div>
          </section>
        </div>

        <section className="goods-receipt-booking__section" aria-labelledby="receipt-search-title">
          <h2 id="receipt-search-title" className="goods-receipt-booking__section-title">
            <MdSearch aria-hidden="true" /> Wareneingang Artikelsuche
          </h2>
          <div className="goods-receipt-booking__section-content">
            <div className="goods-receipt-booking__field-row">
              <div className="goods-receipt-booking__field">
                <label htmlFor="receipt-index-search">Indexsuche</label>
                <input id="receipt-index-search" name="indexSearch" type="search" placeholder="Artikelname oder Artikelnummer" aria-describedby="receipt-search-hint" />
              </div>
              <div className="goods-receipt-booking__field">
                <label htmlFor="receipt-ean">EAN</label>
                <input id="receipt-ean" name="ean" type="text" inputMode="numeric" placeholder="EAN eingeben oder scannen" aria-describedby="receipt-search-hint" />
              </div>
            </div>
            <div className="goods-receipt-booking__search-actions">
              <p id="receipt-search-hint" className="goods-receipt-booking__hint">
                Die Artikelsuche wird später mit den Stammdaten verbunden.
              </p>
              <button
                type="button"
                className="goods-receipt-booking__btn goods-receipt-booking__btn--secondary"
                onClick={() => setIsArticleModalOpen(true)}
              >
                <MdAdd aria-hidden="true" /> Neuen Artikel anlegen
              </button>
            </div>
          </div>
        </section>

        <section className="goods-receipt-booking__section" aria-labelledby="receipt-items-title">
          <h2 id="receipt-items-title" className="goods-receipt-booking__section-title">
            <MdAssignment aria-hidden="true" /> Positionen auf Wareneingang – Lieferschein
          </h2>
          <div className="goods-receipt-booking__table-wrapper" role="region" aria-labelledby="receipt-items-title" tabIndex={0}>
            <table className="goods-receipt-booking__table" aria-labelledby="receipt-items-title">
              <thead>
                <tr>
                  <th scope="col"><input type="checkbox" aria-label="Alle Positionen auswählen" disabled /></th>
                  <th scope="col">Nr.</th>
                  <th scope="col">Beschreibung</th>
                  <th scope="col">Bemerkung</th>
                  <th scope="col">IMEI</th>
                  <th scope="col">Menge</th>
                  <th scope="col">EK</th>
                  <th scope="col">VK</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={8} className="goods-receipt-booking__empty-state">
                    Noch keine Artikel zum Wareneingang hinzugefügt.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="goods-receipt-booking__section" aria-labelledby="receipt-actions-title">
          <h2 id="receipt-actions-title" className="goods-receipt-booking__section-title">
            <MdSettings aria-hidden="true" /> Aktion
          </h2>
          <div className="goods-receipt-booking__footer-actions">
            <button
              type="button"
              className="goods-receipt-booking__btn goods-receipt-booking__btn--secondary"
              title="Keine Positionen zum Löschen vorhanden"
              disabled
            >
              <MdDeleteOutline aria-hidden="true" /> Löschen
            </button>
            <p id="receipt-booking-hint" className="goods-receipt-booking__hint">
              Die Buchung wird später angebunden.
            </p>
            <button
              type="submit"
              className="goods-receipt-booking__btn goods-receipt-booking__btn--primary"
              aria-describedby="receipt-booking-hint"
              disabled
            >
              Wareneingang buchen
            </button>
          </div>
        </section>
      </form>

      {isArticleModalOpen && <NewArticle onClose={() => setIsArticleModalOpen(false)} />}
    </section>
  );
}
